import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { TermsDocument } from 'components/TermsDocument'
import {
  acceptTerms,
  getCurrentTerms,
  getErrorResponseStatus,
  getTermsErrorCode,
  hasErrorResponse,
  TERMS_ERROR_CODES,
} from 'api/terms'
import { branding } from 'constants/branding'
import { routes } from 'constants/routes'
import { authStrings, termsStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { toErrorMessage } from 'hooks/utils'
import {
  fetchLatestTermsStatus,
  termsStatusQueryKey,
  useTermsStatus,
} from 'hooks/useTermsStatus'
import {
  clearTermsAcceptanceUnconfirmed,
  clearTermsConsentSignOutPending,
  hasUnconfirmedTermsAcceptance,
  isTermsConsentSignOutPending,
  markTermsAcceptanceUnconfirmed,
  markTermsConsentSignOutPending,
} from 'lib/termsGate'
import { unsubscribeFromWebPush } from 'lib/webPush'
import type { TTermsStatus } from 'types/terms'

const strings = termsStrings.consent

/**
 * 利用規約の同意画面（/terms/consent）。AppLayout の外に置き、PubNub 接続・通知登録・
 * 業務APIを開始しない。未ログインならこの画面で本人認証し、認証した本人にだけ同意を記録する。
 */
export default function TermsConsent() {
  const { user, loading, signIn, captureSession } = useAuth()
  // ログインの失敗はここで保持する。ログイン処理の途中で利用者が一時的に現れても（停止中・
  // 状態確認の失敗などでログアウトに戻る）、入力欄と失敗の案内を失わない
  const [signInError, setSignInError] = useState<string | undefined>()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const uid = user?.uid

  // 利用者が現れたら、それより前のログイン失敗の案内は消す（別の利用者へ古い案内を出さない）
  useEffect(() => {
    if (uid !== undefined) setSignInError(undefined)
  }, [uid])

  const handleSignIn = async (email: string, password: string) => {
    if (isSigningIn) return
    setSignInError(undefined)
    setIsSigningIn(true)
    const errorMessage = await signIn(email, password)
    // 未ログインに戻ったときだけ案内する（その間に別の利用者がログインしていたら出さない）
    if (errorMessage && captureSession().uid === undefined) {
      setSignInError(errorMessage)
    }
    setIsSigningIn(false)
  }

  return (
    <TermsConsentShell>
      {loading ? (
        <p className="text-sm text-muted">{authStrings.checkingSessionBody}</p>
      ) : user && !isSigningIn ? (
        <TermsConsentForUser key={user.uid} uid={user.uid} />
      ) : (
        <TermsConsentSignIn
          error={signInError}
          isSubmitting={isSigningIn}
          onSubmit={handleSignIn}
        />
      )}
    </TermsConsentShell>
  )
}

function TermsConsentShell({ children }: { children: ReactNode }) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-surface text-ink">
      <div className="relative grid min-h-screen place-items-center px-5 py-10 sm:px-6">
        <section className="grid w-full max-w-[760px] gap-6 rounded-[32px] border border-white/70 bg-white/90 p-6 shadow-soft backdrop-blur-sm sm:p-8">
          <header className="grid justify-items-center gap-3 text-center">
            <img
              src={branding.logoUrl}
              alt={branding.serviceName}
              className="h-auto w-full max-w-[180px]"
            />
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted">
              {strings.kicker}
            </span>
            <h1 className="text-2xl font-display text-ink">{strings.title}</h1>
          </header>
          {children}
        </section>
      </div>
    </main>
  )
}

function TermsConsentSignIn({
  error,
  isSubmitting,
  onSubmit,
}: {
  error: string | undefined
  isSubmitting: boolean
  onSubmit: (email: string, password: string) => Promise<void>
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void onSubmit(email, password)
  }

  return (
    <form className="mx-auto grid w-full max-w-[440px] gap-4" onSubmit={handleSubmit}>
      <p className="text-sm text-muted">{strings.loginLead}</p>
      <label className="grid gap-2 text-sm text-ink">
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
          {authStrings.emailLabel}
        </span>
        <Input
          type="email"
          name="email"
          autoComplete="email"
          placeholder={authStrings.emailPlaceholder}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </label>
      <label className="grid gap-2 text-sm text-ink">
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
          {authStrings.passwordLabel}
        </span>
        <Input
          type="password"
          name="password"
          autoComplete="current-password"
          placeholder={authStrings.passwordPlaceholder}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </label>
      {error && <Alert>{error}</Alert>}
      <Button type="submit" disabled={isSubmitting} fullWidth>
        {isSubmitting ? authStrings.working : authStrings.signIn}
      </Button>
    </form>
  )
}

type TermsSignOutResult =
  | { status: 'signedOut' }
  | { status: 'failed'; message: string }
  /** 途中で別の利用者へ切り替わったため、その利用者を操作せずに中止した */
  | { status: 'aborted' }

/**
 * 本人（uid）をログアウトしてログイン画面へ戻る（顧客データのキャッシュは AuthContext の signOut が消す）。
 * 同意保存後の完了処理と、同意せずにログアウトする場合で共通に使う。開始時の利用者が本人でない、
 * または await の間に別の利用者へ切り替わったら、その利用者のログアウト・印・画面遷移には手を出さない。
 */
function useTermsSignOut() {
  const { signOut, captureSession } = useAuth()
  const navigate = useNavigate()

  return useCallback(
    async (uid: string, snackbarMessage?: string): Promise<TermsSignOutResult> => {
      const session = captureSession()
      if (session.uid !== uid) return { status: 'aborted' }

      await unsubscribeFromWebPush(uid).catch(() => {
        // 購読解除に失敗してもログアウトは続行する
      })
      if (!session.isCurrent()) return { status: 'aborted' }

      const errorMessage = await signOut()
      if (errorMessage) return { status: 'failed', message: errorMessage }

      // 本人のログアウトは完了した（本人の印だけを消す）。その後に別の利用者がログインしていたら、
      // その利用者の画面は遷移させない
      clearTermsConsentSignOutPending(uid)
      if (captureSession().uid !== undefined) return { status: 'aborted' }
      navigate(routes.LOGIN, {
        replace: true,
        state: snackbarMessage ? { snackbarMessage } : null,
      })
      return { status: 'signedOut' }
    },
    [captureSession, navigate, signOut],
  )
}

function TermsConsentForUser({ uid }: { uid: string }) {
  const [signOutPending, setSignOutPending] = useState(() =>
    isTermsConsentSignOutPending(uid),
  )
  // 同意の送信結果が未確認のまま開いた（応答が失われた後の再読込・別タブ・業務画面からの案内）
  const [checkingUnconfirmed, setCheckingUnconfirmed] = useState(() =>
    hasUnconfirmedTermsAcceptance(uid),
  )
  const status = useTermsStatus()

  /** 本人の同意の保存を確かめた。ログアウト待ちの印を残してから未確認の印を消す */
  const completeAcceptance = useCallback(() => {
    markTermsConsentSignOutPending(uid)
    clearTermsAcceptanceUnconfirmed(uid)
    setSignOutPending(true)
  }, [uid])
  const finishUnconfirmedCheck = useCallback(() => setCheckingUnconfirmed(false), [])

  if (signOutPending) {
    return <TermsConsentCompleted uid={uid} />
  }

  // 送信結果が未確認のまま業務を利用できる状態に見えたら（保存されていた等）、
  // 通常の案内（業務画面へ進む）を出す前に、送信より後の本人の状態で確かめる
  if (
    checkingUnconfirmed ||
    (status.data?.businessAccessAllowed && hasUnconfirmedTermsAcceptance(uid))
  ) {
    return (
      <UnconfirmedAcceptanceCheck
        uid={uid}
        onAccepted={completeAcceptance}
        onSettled={finishUnconfirmedCheck}
      />
    )
  }

  if (!status.data) {
    return status.isError ? (
      <StatusMessage
        uid={uid}
        title={termsStrings.gate.statusFailedTitle}
        body={toErrorMessage(status.error, termsStrings.gate.statusFailedBody)}
        tone="error"
        onRetry={() => {
          void status.refetch()
        }}
      />
    ) : (
      <p className="text-center text-sm text-muted">{termsStrings.gate.checking}</p>
    )
  }

  return (
    <TermsConsentByState
      uid={uid}
      status={status.data}
      onAccepted={completeAcceptance}
    />
  )
}

/**
 * 同意の送信結果が未確認（送信後に応答が失われた・送信中に再読込した）。送信より後に取得した
 * 本人の状態で確かめ、同意済みならログアウトの完了処理へ進める（業務画面へ直接は進めない）。
 * 同意が不要な状態（同意確認の無効化など）なら印を消して通常の案内へ戻す。同意が必要な状態のままなら
 * 印を残して同意画面を表示する（遅れて保存された場合も完了処理へ戻せるように）。
 * 確かめられない間は、業務画面へも完了画面へも進めない。
 */
function UnconfirmedAcceptanceCheck({
  uid,
  onAccepted,
  onSettled,
}: {
  uid: string
  onAccepted: () => void
  onSettled: () => void
}) {
  const queryClient = useQueryClient()
  const { captureSession } = useAuth()
  const [error, setError] = useState<string | undefined>()
  const started = useRef(false)

  const check = useCallback(async () => {
    const session = captureSession()
    if (session.uid !== uid) return
    setError(undefined)
    try {
      const latest = await fetchLatestTermsStatus(queryClient, uid)
      // 確かめる間に別の利用者へ切り替わったら、印にも画面にも手を出さない
      if (!session.isCurrent()) return
      if (latest.state === 'ACCEPTED') {
        onAccepted()
        return
      }
      if (latest.businessAccessAllowed) clearTermsAcceptanceUnconfirmed(uid)
      onSettled()
    } catch (caught) {
      if (!session.isCurrent()) return
      setError(
        toErrorMessage(
          caught instanceof Error ? caught : null,
          termsStrings.gate.statusFailedBody,
        ),
      )
    }
  }, [captureSession, onAccepted, onSettled, queryClient, uid])

  useEffect(() => {
    if (started.current) return
    started.current = true
    void check()
  }, [check])

  return error ? (
    <StatusMessage
      uid={uid}
      title={termsStrings.gate.statusFailedTitle}
      body={error}
      tone="error"
      onRetry={() => {
        void check()
      }}
    />
  ) : (
    <p className="text-center text-sm text-muted">{termsStrings.gate.checking}</p>
  )
}

function TermsConsentByState({
  uid,
  status,
  onAccepted,
}: {
  uid: string
  status: TTermsStatus
  onAccepted: () => void
}) {
  switch (status.state) {
    case 'ACCEPTANCE_REQUIRED':
    case 'REACCEPTANCE_REQUIRED':
      return (
        <TermsConsentForm
          uid={uid}
          isReacceptance={status.state === 'REACCEPTANCE_REQUIRED'}
          requiredVersionId={status.requiredVersion?.id}
          onAccepted={onAccepted}
        />
      )
    case 'VERIFICATION':
      // 運営の動作確認中（同意必須OFF）。同意を登録せず、案内だけを表示する
      return (
        <StatusMessage
          uid={uid}
          title={strings.verificationTitle}
          body={strings.verificationBody}
          showGoToApp
        />
      )
    case 'UNAVAILABLE':
      return (
        <StatusMessage
          uid={uid}
          title={strings.unavailableTitle}
          body={strings.unavailableBody}
          tone="error"
        />
      )
    case 'ACCEPTED':
      return (
        <StatusMessage
          uid={uid}
          title={strings.acceptedTitle}
          body={strings.acceptedBody}
          showGoToApp
        />
      )
    default:
      return (
        <StatusMessage
          uid={uid}
          title={strings.notRequiredTitle}
          body={strings.notRequiredBody}
          showGoToApp
        />
      )
  }
}

function TermsConsentForm({
  uid,
  isReacceptance,
  requiredVersionId,
  onAccepted,
}: {
  uid: string
  isReacceptance: boolean
  requiredVersionId: number | undefined
  onAccepted: () => void
}) {
  const queryClient = useQueryClient()
  const { captureSession } = useAuth()
  const current = useQuery({
    queryKey: ['terms-current', uid, requiredVersionId],
    // 表示している本人のトークンでだけ取得する
    queryFn: () => getCurrentTerms(uid),
    retry: 1,
  })
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [notice, setNotice] = useState<string | undefined>()

  const reloadLatest = async () => {
    setNotice(strings.versionChanged)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: termsStatusQueryKey(uid) }),
      current.refetch(),
    ])
  }

  const handleAccept = async () => {
    if (!current.data || isAccepting) return
    // 表示中の本人と今の利用者が違う（切り替え直後で画面が古い）なら送らない
    const session = captureSession()
    if (session.uid !== uid) return
    const versionId = current.data.id
    setError(undefined)
    setNotice(undefined)
    setIsAccepting(true)
    // 応答が失われても再読込後に本人の状態で保存の有無を確かめられるよう、送信前に印を残す
    markTermsAcceptanceUnconfirmed(uid, versionId)
    try {
      // 送信時の利用者が本人でなければ、トークンを使う前に失敗させる（他人の同意として記録しない）
      const result = await acceptTerms(versionId, uid)
      // 本人のトークンで記録済みでも、その後に別の利用者へ切り替わっていたらその利用者を操作しない
      if (!session.isCurrent()) return
      if (!result.businessAccessAllowed) {
        // 応答は届いた: 再送で過去の同意が見つかったが、その間に新しい版が公開されていた
        clearTermsAcceptanceUnconfirmed(uid)
        await reloadLatest()
        return
      }
      onAccepted()
    } catch (caught) {
      if (!session.isCurrent()) return
      // サーバーが拒否した（4xx）なら保存されていない。通信断・5xx は保存済みの可能性があるので印を残す
      const responseStatus = getErrorResponseStatus(caught)
      if (responseStatus !== undefined && responseStatus < 500) {
        clearTermsAcceptanceUnconfirmed(uid)
      }
      if (getTermsErrorCode(caught) === TERMS_ERROR_CODES.versionChanged) {
        await reloadLatest()
        return
      }
      // 保存できなかった（または応答が届かなかった）。同じ版への再送は二重記録されない
      setError(
        hasErrorResponse(caught) && caught instanceof Error && caught.message
          ? caught.message
          : strings.acceptFailed,
      )
    } finally {
      setIsAccepting(false)
    }
  }

  if (current.isPending) {
    return <p className="text-center text-sm text-muted">{termsStrings.gate.checking}</p>
  }

  if (!current.data) {
    return (
      <StatusMessage
        uid={uid}
        title={strings.loadFailed}
        body={toErrorMessage(current.error, strings.unavailableBody)}
        tone="error"
        onRetry={() => {
          void current.refetch()
        }}
      />
    )
  }

  return (
    <div className="grid gap-5">
      <p className="text-sm text-ink">
        {isReacceptance ? strings.reacceptLead : strings.acceptLead}
      </p>
      {notice && (
        <div
          role="status"
          className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {notice}
        </div>
      )}
      <TermsDocument
        serviceName={current.data.serviceName}
        title={current.data.title}
        versionLabel={current.data.versionLabel}
        bodyMarkdown={current.data.bodyMarkdown}
        publishedAt={current.data.publishedAt}
      />
      {error && <Alert>{error}</Alert>}
      <div className="grid gap-3 sm:grid-cols-2">
        <SignOutButton uid={uid} />
        <Button
          type="button"
          onClick={() => {
            void handleAccept()
          }}
          loading={isAccepting}
          aria-label={strings.acceptButton}
        >
          {isAccepting ? strings.accepting : strings.acceptButton}
        </Button>
      </div>
    </div>
  )
}

/**
 * 同意保存後の完了画面。ログアウトを自動で1回試み、失敗したら再試行を案内する。
 * 再読込・別タブでもログアウト待ちの印からこの画面に戻り、通常画面へは自動遷移しない。
 */
function TermsConsentCompleted({ uid }: { uid: string }) {
  const signOutAndLeave = useTermsSignOut()
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const attempted = useRef(false)

  const completeSignOut = useCallback(async () => {
    setIsSigningOut(true)
    setError(undefined)
    const result = await signOutAndLeave(uid, strings.signedOutAfterConsent)
    if (result.status === 'failed') setError(strings.signOutFailed)
    if (result.status !== 'signedOut') setIsSigningOut(false)
  }, [signOutAndLeave, uid])

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true
    void completeSignOut()
  }, [completeSignOut])

  return (
    <div className="grid gap-4 text-center">
      <h2 className="text-lg font-bold text-ink">{strings.completedTitle}</h2>
      <p className="text-sm text-muted">{strings.completedBody}</p>
      {error && <Alert>{error}</Alert>}
      <Button
        type="button"
        onClick={() => {
          void completeSignOut()
        }}
        loading={isSigningOut}
      >
        {error ? strings.retrySignOut : strings.signingOut}
      </Button>
    </div>
  )
}

function SignOutButton({ uid }: { uid: string }) {
  const signOutAndLeave = useTermsSignOut()
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [error, setError] = useState<string | undefined>()

  return (
    <div className="grid gap-2">
      <Button
        type="button"
        variant="outline"
        loading={isSigningOut}
        onClick={async () => {
          setIsSigningOut(true)
          setError(undefined)
          const result = await signOutAndLeave(uid)
          if (result.status === 'failed') setError(result.message)
          if (result.status !== 'signedOut') setIsSigningOut(false)
        }}
      >
        {strings.signOut}
      </Button>
      {error && <Alert>{error}</Alert>}
    </div>
  )
}

function StatusMessage({
  uid,
  title,
  body,
  tone = 'info',
  showGoToApp = false,
  onRetry,
}: {
  uid: string
  title: string
  body: string
  tone?: 'info' | 'error'
  showGoToApp?: boolean
  onRetry?: () => void
}) {
  const navigate = useNavigate()

  return (
    <div className="grid gap-4">
      <div className="grid gap-2 text-center">
        <h2 className="text-lg font-bold text-ink">{title}</h2>
        {tone === 'error' ? (
          <Alert>{body}</Alert>
        ) : (
          <p className="text-sm text-muted">{body}</p>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <SignOutButton uid={uid} />
        {showGoToApp && (
          <Button type="button" onClick={() => navigate(routes.HOME)}>
            {strings.goToApp}
          </Button>
        )}
        {onRetry && (
          <Button type="button" onClick={onRetry}>
            {termsStrings.gate.retry}
          </Button>
        )}
      </div>
    </div>
  )
}
